import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import DownhillSkiingOutlinedIcon from '@mui/icons-material/DownhillSkiingOutlined';
import PedalBikeOutlinedIcon from '@mui/icons-material/PedalBikeOutlined';
import { useNavigate } from 'react-router-dom';
import { countries } from 'countries-list';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import 'react-international-phone/style.css';
import './BookingForm.css';

import { MuiPhone } from './MuiPhone';
import { URL_PROXY } from './apiConfig';

const countryOptions = Object.keys(countries)
  .map((countryCode) => ({ value: countryCode.toLowerCase(), label: countries[countryCode].name }))
  .sort((a, b) => a.label.localeCompare(b.label));

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DATE_PATTERN = /^\d{2}-\d{2}-\d{4}$/;

const isValidBookingData = (data) => {
  if (!data || typeof data !== 'object') return false;
  const persons = Number(data.Persons);
  const price = Number(data.price);
  return (
    typeof data.OriginAdress === 'string' && data.OriginAdress.trim().length > 0 &&
    typeof data.DestinationAdress === 'string' && data.DestinationAdress.trim().length > 0 &&
    Number.isInteger(persons) && persons >= 1 && persons <= 7 &&
    typeof data.Date === 'string' && DATE_PATTERN.test(data.Date) &&
    typeof data.Time === 'string' && TIME_PATTERN.test(data.Time) &&
    Number.isFinite(price) && price > 0 &&
    typeof data.quoteToken === 'string' && data.quoteToken.length > 20
  );
};

const BookingForm = ({ bookingData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bookingIsValid = useMemo(() => isValidBookingData(bookingData), [bookingData]);

  const amenities = [
    { id: 'child_seat', label: t('form_child_safety_seat'), Icon: EventSeatOutlinedIcon },
    { id: 'bikes', label: t('form_bikes'), Icon: PedalBikeOutlinedIcon },
    { id: 'skis', label: t('form_skis'), Icon: DownhillSkiingOutlinedIcon },
  ];

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    address: '',
    addressAddition: '',
    selectedCountry: 'it',
    muiPhone: '+39',
    email: '',
    verifiedEmail: '',
    additionalInfo: '',
  });
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!bookingIsValid) navigate('/', { replace: true });
  }, [bookingIsValid, navigate]);

  if (!bookingIsValid) {
    return <div className="booking-form-loading"><CircularProgress /></div>;
  }

  const { Date, DestinationAdress, OriginAdress, Persons, Time, price, quoteToken } = bookingData;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: undefined }));
    setSubmitError('');
  };

  const toggleAmenity = (id) => {
    setSelectedAmenities((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const validateForm = () => {
    const errors = {};
    const phoneDigits = form.muiPhone.replace(/\D/g, '');

    if (!form.firstName.trim()) errors.firstName = t('form_error_required');
    else if (form.firstName.trim().length > 40) errors.firstName = t('form_error_name_length');
    if (!form.lastName.trim()) errors.lastName = t('form_error_required');
    else if (form.lastName.trim().length > 40) errors.lastName = t('form_error_name_length');
    if (!form.address.trim()) errors.address = t('form_error_required');
    else if (form.address.trim().length > 100) errors.address = t('form_error_address_length');
    if (form.addressAddition.trim().length > 100) errors.addressAddition = t('form_error_address_length');
    if (!form.selectedCountry) errors.selectedCountry = t('form_error_required');
    if (!EMAIL_PATTERN.test(form.email.trim()) || form.email.trim().length > 254) {
      errors.email = t('form_error_email');
    }
    if (form.email.trim().toLowerCase() !== form.verifiedEmail.trim().toLowerCase()) {
      errors.verifiedEmail = t('form_error_email_match');
    }
    if (phoneDigits.length < 7 || phoneDigits.length > 15) errors.muiPhone = t('form_error_phone');
    if (form.additionalInfo.trim().length > 1000) errors.additionalInfo = t('form_error_info_length');

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || !validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const requestPayload = {
        Date,
        DestinationAdress,
        OriginAdress,
        Persons,
        Time,
        price,
        selectedAmenities,
        selectedCountry: form.selectedCountry,
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        address: form.address.trim(),
        muiPhone: form.muiPhone,
        additionalInfo: form.additionalInfo.trim(),
        addressAddition: form.addressAddition.trim(),
        quoteToken,
      };
      const response = await axios.post(`${URL_PROXY}/requestBooking`, requestPayload);

      if (response.data.success && response.data.bookingId) {
        const confirmation = response.data.confirmation || {
          bookingId: response.data.bookingId,
          firstName: requestPayload.firstName,
          lastName: requestPayload.lastName,
          email: requestPayload.email,
          phonenumber: requestPayload.muiPhone,
          origin: requestPayload.OriginAdress,
          destination: requestPayload.DestinationAdress,
          persons: requestPayload.Persons,
          date: requestPayload.Date,
          time: requestPayload.Time,
          price: requestPayload.price,
          amenities: requestPayload.selectedAmenities,
          status: 'PROCESSING',
        };
        sessionStorage.setItem('moroderBookingConfirmation', JSON.stringify(confirmation));
        navigate('/bookingConfirmation', { state: { confirmation } });
      } else {
        setSubmitError(t('form_error_submit'));
      }
    } catch (error) {
      const serverMessage = error.response?.data?.message;
      setSubmitError(serverMessage || t('form_error_submit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="booking-form-page">
      <section className="booking-form-card">
        <header className="booking-form-header">
          <button className="booking-form-back" type="button" onClick={() => navigate('/')}>
            <ArrowBackRoundedIcon />
            <span>{t('form_back')}</span>
          </button>
          <span className="booking-form-kicker">{t('form_kicker')}</span>
        </header>

        <div className="booking-form-intro">
          <div>
            <h1>{t('form_personal_information')}</h1>
            <p>{t('form_intro')}</p>
          </div>
          <span className="booking-form-status"><i />{t('form_status')}</span>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="booking-form-layout">
            <section className="booking-form-panel booking-form-fields" aria-labelledby="customer-details-title">
              <div className="booking-form-section-heading">
                <span>01</span>
                <div><h2 id="customer-details-title">{t('form_customer_details')}</h2><p>{t('form_customer_hint')}</p></div>
              </div>

              <div className="booking-form-field-grid">
                <TextField label={t('form_first_name')} value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} error={!!formErrors.firstName} helperText={formErrors.firstName} inputProps={{ maxLength: 40, autoComplete: 'given-name' }} required />
                <TextField label={t('form_last_name')} value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} error={!!formErrors.lastName} helperText={formErrors.lastName} inputProps={{ maxLength: 40, autoComplete: 'family-name' }} required />
                <TextField className="booking-form-span-2" label={t('form_address')} value={form.address} onChange={(e) => updateField('address', e.target.value)} error={!!formErrors.address} helperText={formErrors.address} inputProps={{ maxLength: 100, autoComplete: 'street-address' }} required />
                <TextField className="booking-form-span-2" label={t('form_additional_address_details')} value={form.addressAddition} onChange={(e) => updateField('addressAddition', e.target.value)} error={!!formErrors.addressAddition} helperText={formErrors.addressAddition} inputProps={{ maxLength: 100 }} />
                <FormControl error={!!formErrors.selectedCountry} required>
                  <InputLabel>{t('form_country')}</InputLabel>
                  <Select value={form.selectedCountry} label={t('form_country')} onChange={(e) => updateField('selectedCountry', e.target.value)}>
                    {countryOptions.map((country) => <MenuItem key={country.value} value={country.value}>{country.label}</MenuItem>)}
                  </Select>
                  <FormHelperText>{formErrors.selectedCountry}</FormHelperText>
                </FormControl>
                <MuiPhone fullWidth label={t('form_phone_number')} value={form.muiPhone} onChange={(value) => updateField('muiPhone', value)} error={!!formErrors.muiPhone} helperText={formErrors.muiPhone} required />
                <TextField label="Email" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} error={!!formErrors.email} helperText={formErrors.email} inputProps={{ maxLength: 254, autoComplete: 'email' }} required />
                <TextField label={t('form_verify_email')} type="email" value={form.verifiedEmail} onChange={(e) => updateField('verifiedEmail', e.target.value)} error={!!formErrors.verifiedEmail} helperText={formErrors.verifiedEmail} inputProps={{ maxLength: 254, autoComplete: 'off' }} required />
              </div>
            </section>

            <aside className="booking-form-side">
              <section className="booking-form-panel booking-summary" aria-labelledby="booking-summary-title">
                <div className="booking-form-section-heading compact"><span>02</span><div><h2 id="booking-summary-title">{t('form_booking_sumary')}</h2><p>{t('form_summary_hint')}</p></div></div>
                <dl>
                  <div><dt><PlaceOutlinedIcon />{t('form_start')}</dt><dd>{OriginAdress}</dd></div>
                  <div><dt><PlaceOutlinedIcon />{t('form_destination')}</dt><dd>{DestinationAdress}</dd></div>
                  <div className="booking-summary-inline"><span><dt><CalendarMonthOutlinedIcon />{t('booking_date')}</dt><dd>{Date}</dd></span><span><dt><AccessTimeOutlinedIcon />{t('booking_time')}</dt><dd>{Time}</dd></span><span><dt><PersonOutlineRoundedIcon />{t('booking_persons')}</dt><dd>{Persons}</dd></span></div>
                </dl>
                <div className="booking-summary-price"><span>{t('form_price')}</span><strong>€ {Number(price).toFixed(2)}</strong></div>
              </section>

              <section className="booking-form-panel booking-extras" aria-labelledby="booking-extras-title">
                <div className="booking-form-section-heading compact"><span>03</span><div><h2 id="booking-extras-title">{t('form_additional_details')}</h2><p>{t('form_extras_hint')}</p></div></div>
                <div className="booking-amenities">
                  {amenities.map(({ id, label, Icon }) => (
                    <FormControlLabel key={id} className={selectedAmenities.includes(id) ? 'selected' : ''} control={<Checkbox checked={selectedAmenities.includes(id)} onChange={() => toggleAmenity(id)} />} label={<><Icon /><span>{label}</span></>} />
                  ))}
                </div>
                <TextField fullWidth multiline minRows={3} label={t('form_additional_flight_train_pickup_info')} value={form.additionalInfo} onChange={(e) => updateField('additionalInfo', e.target.value)} error={!!formErrors.additionalInfo} helperText={formErrors.additionalInfo || `${form.additionalInfo.length}/1000`} inputProps={{ maxLength: 1000 }} />
              </section>
            </aside>
          </div>

          <div className="booking-form-submit">
            <p>{t('form_ride_request_note')}</p>
            <div>
              {submitError && <p className="booking-form-submit-error" role="alert">{submitError}</p>}
              <button type="submit" disabled={isSubmitting}>{isSubmitting ? <><CircularProgress size={20} />{t('form_submitting')}</> : t('form_reserve_ride')}</button>
            </div>
          </div>
        </form>
      </section>
    </main>
  );
};

export default BookingForm;
